import React from 'react';
import { RotateCcw } from 'lucide-react';
import { SectionCard } from '../common/SectionCard';
import { ScoreChip } from '../common/ScoreChip';
import { Button } from '../ui/Button';
import { Slider } from '../ui/Slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/Select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/Table';
import { usePlatform } from '../../contexts/PlatformContext';
import { criteriaSets } from '../../data/criteria';
import { scoreBand, weightedScore, bandLabel } from '../../utils/scoring';
import { STAGE_META } from '../../utils/stages';
export function ScoringEngine() {
  const {
    applications,
    weights,
    bands,
    setWeight,
    setBands
  } = usePlatform();
  const [setId, setSetId] = React.useState(criteriaSets[0].id);
  const criteriaSet = criteriaSets.find(set => set.id === setId) ?? criteriaSets[0];
  const setWeights = weights[criteriaSet.id] ?? {};
  const setBandValues = bands[criteriaSet.id] ?? criteriaSet.bands;
  const total = Object.values(setWeights).reduce((sum, value) => sum + value, 0);
  const affected = applications.filter(record => record.track === criteriaSet.track && record.criteria.length > 0);
  const preview = affected.map(record => {
    const original = weightedScore(record.criteria);
    const revised = weightedScore(record.criteria, setWeights);
    return {
      record,
      original,
      revised,
      originalBand: scoreBand(original, criteriaSet.bands),
      revisedBand: scoreBand(revised, setBandValues)
    };
  });
  const bandChanges = preview.filter(row => row.originalBand !== row.revisedBand).length;
  function resetWeights() {
    criteriaSet.criteria.forEach(criterion => {
      setWeight(criteriaSet.id, criterion.id, criterion.weight);
    });
    setBands(criteriaSet.id, {
      autoAdvance: criteriaSet.bands.autoAdvance,
      manualReview: criteriaSet.bands.manualReview
    });
  }
  return <div className="space-y-4" data-id="element-277" data-mp="tjo3egupzqck">
      <SectionCard title="Preset criteria set" description="Criteria sets are configured per track and per stage. Weights apply to every record scored under the set." actions={<div className="flex items-center gap-2" data-id="element-279" data-mp="138frgdwntqi7">
            <Select value={setId} onValueChange={setSetId} data-id="element-280" data-mp="yao29rnegihp">
              <SelectTrigger className="w-[280px]" aria-label="Criteria set" data-id="element-281" data-mp="ybfv4upm424a">
                <SelectValue data-id="element-282" data-mp="aluzbb8d1adl" />
              </SelectTrigger>
              <SelectContent data-id="element-283" data-mp="5m8kwygspo37">
                {criteriaSets.map(set => <SelectItem key={set.id} value={set.id} data-id="element-284" data-mp="5oczcacbm5hq">
                    {set.label}
                  </SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={resetWeights} data-id="element-285" data-mp="fm99aytiuue9">
              <RotateCcw className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" data-id="element-286" data-mp="1vx3f2g4vsryx" />
              Reset
            </Button>
          </div>} data-id="element-278" data-mp="timqs7y22vsq">
        <div className="flex flex-wrap items-center gap-4 text-[12px] text-muted-foreground" data-id="element-287" data-mp="1yiufwk3a8dgi">
          <span data-id="element-288" data-mp="23hxky4j9ijoh">
            Track:{' '}
            <span className="text-foreground capitalize" data-id="element-289" data-mp="23iyvm7wd945o">{criteriaSet.track}</span>
          </span>
          <span data-id="element-290" data-mp="1pj7p0s86iq1p">
            Stage:{' '}
            <span className="text-foreground" data-id="element-291" data-mp="1pk92128r9mm2">{STAGE_META[criteriaSet.stage].label}</span>
          </span>
          <span data-id="element-292" data-mp="1tlksf9zgebvb">
            Criteria: <span className="text-foreground" data-id="element-293" data-mp="2h8tzwsk9n2d2">{criteriaSet.criteria.length}</span>
          </span>
          <span className={total === 100 ? 'text-uae-leaf' : 'text-uae-red'} data-id="element-294" data-mp="2m8yh6kt0014c">
            Weights total {total}%
            {total !== 100 && ' — scores are normalised until this equals 100%'}
          </span>
        </div>

        <ul className="mt-4 space-y-4" data-id="element-295" data-mp="2chkde0u0kf5e">
          {criteriaSet.criteria.map(criterion => <li key={criterion.id} data-id="element-296" data-mp="30756hcjvssse">
              <div className="flex items-baseline justify-between gap-3" data-id="element-297" data-mp="3063iy8loidts">
                <div className="min-w-0" data-id="element-298" data-mp="34zpw01vr8jq4">
                  <p className="text-[13px] font-medium" data-id="element-299" data-mp="1halcwuvrbrqz">{criterion.label}</p>
                  <p className="text-[11px] leading-relaxed text-muted-foreground" data-id="element-300" data-mp="1h8id5j4syk92">
                    {criterion.description}
                  </p>
                </div>
                <span className="shrink-0 font-mono text-[12px]" data-id="element-301" data-mp="17d6wuwdqx4z7">
                  {setWeights[criterion.id] ?? criterion.weight}%
                </span>
              </div>
              <div className="mt-2" data-id="element-302" data-mp="201mdfncizvcn">
                <Slider value={[setWeights[criterion.id] ?? criterion.weight]} max={50} step={1} onValueChange={value => setWeight(criteriaSet.id, criterion.id, value[0])} aria-label={`${criterion.label} weight`} data-id="element-303" data-mp="200l0fdby8ysa" />
              </div>
            </li>)}
        </ul>
      </SectionCard>

      <div className="grid gap-4 xl:grid-cols-[1fr_1.6fr]" data-id="element-304" data-mp="2isvklpytq812">
        <SectionCard title="Threshold bands" description="Bands decide whether a record is recommended to advance, held for manual review, or recommended for decline." data-id="element-305" data-mp="2e0ly6jwx3q3j">
          <div className="space-y-5" data-id="element-306" data-mp="5gj2p988gnk6">
            <div data-id="element-307" data-mp="5hkr0fe9f6ju">
              <div className="flex items-baseline justify-between" data-id="element-308" data-mp="5j5ke1ou8jg4">
                <p className="text-[13px] font-medium" data-id="element-309" data-mp="9hcqm2loe2d2">Auto-advance at or above</p>
                <span className="font-mono text-[12px]" data-id="element-310" data-mp="ej20vg12qc03">{setBandValues.autoAdvance}</span>
              </div>
              <div className="mt-2" data-id="element-311" data-mp="jgao3v12x687">
                <Slider value={[setBandValues.autoAdvance]} max={100} step={1} onValueChange={value => setBands(criteriaSet.id, {
                ...setBandValues,
                autoAdvance: Math.max(value[0], setBandValues.manualReview + 1)
              })} aria-label="Auto advance threshold" data-id="element-312" data-mp="jf99jgkuu1lq" />
              </div>
            </div>
            <div data-id="element-313" data-mp="174boggo3tgcj">
              <div className="flex items-baseline justify-between" data-id="element-314" data-mp="339jev5uch03f">
                <p className="text-[13px] font-medium" data-id="element-315" data-mp="337qa5w3akpy2">Manual review at or above</p>
                <span className="font-mono text-[12px]" data-id="element-316" data-mp="2ycjb51kyw9fh">{setBandValues.manualReview}</span>
              </div>
              <div className="mt-2" data-id="element-317" data-mp="3d1p62dcrvdhk">
                <Slider value={[setBandValues.manualReview]} max={100} step={1} onValueChange={value => setBands(criteriaSet.id, {
                ...setBandValues,
                manualReview: Math.min(value[0], setBandValues.autoAdvance - 1)
              })} aria-label="Manual review threshold" data-id="element-318" data-mp="388dy28w84qo2" />
              </div>
            </div>
            <div className="rounded-sm border border-border bg-muted/50 px-3 py-2.5 text-[12px] leading-relaxed text-muted-foreground" data-id="element-319" data-mp="21jxkidkavd0f">
              Below {setBandValues.manualReview} a record is recommended for decline. Bands are
              advisory only — the assigned officer records the decision.
            </div>
          </div>
        </SectionCard>

        <SectionCard title="Live re-score preview" description={`${bandChanges} of ${preview.length} records would change band under the current configuration.`} bodyClassName="p-0" data-id="element-320" data-mp="h4hj24qfotog">
          <div className="overflow-x-auto" data-id="element-321" data-mp="3pihtp6hz7oyk">
            <Table data-id="element-322" data-mp="3phf6b8jv44ne">
              <TableHeader data-id="element-323" data-mp="3pgx1von1qk4e">
                <TableRow data-id="element-324" data-mp="3uhb5muhm77wm">
                  <TableHead className="min-w-[220px]" data-id="element-325" data-mp="3uhkxknm7zp4y">Record</TableHead>
                  <TableHead className="text-right" data-id="element-326" data-mp="3fkaeot78zgz7">Current</TableHead>
                  <TableHead className="text-right" data-id="element-327" data-mp="3fm53iiln3t8k">Revised</TableHead>
                  <TableHead data-id="element-328" data-mp="3kjwth72q55a4">Resulting band</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody data-id="element-329" data-mp="1dt72hzhplucj">
                {preview.map(row => <TableRow key={row.record.id} data-id="element-330" data-mp="1is0ko57xy9j1">
                    <TableCell data-id="element-331" data-mp="1ipo15nmjbr3v">
                      <p className="text-[13px] leading-snug" data-id="element-332" data-mp="6iwzs5u2cc3j">{row.record.eventName}</p>
                      <p className="font-mono text-[11px] text-muted-foreground" data-id="element-333" data-mp="1jc3y4a6wjv1">
                        {row.record.ref}
                      </p>
                    </TableCell>
                    <TableCell className="text-right" data-id="element-334" data-mp="3svn56gkycuyu">
                      <ScoreChip score={row.original} bands={criteriaSet.bands} data-id="element-335" data-mp="3nxvh5xnfhszx" />
                    </TableCell>
                    <TableCell className="text-right" data-id="element-336" data-mp="3j1dcsyw0fwo4">
                      <ScoreChip score={row.revised} bands={setBandValues} data-id="element-337" data-mp="3eygs92mwtqhj" />
                    </TableCell>
                    <TableCell data-id="element-338" data-mp="39ylymrbr2mss">
                      <span className={`text-[12px] ${row.originalBand !== row.revisedBand ? 'font-medium text-uae-red' : 'text-muted-foreground'}`} data-id="element-339" data-mp="39znm5v9yd1re">
                        {bandLabel(row.revisedBand)}
                        {row.originalBand !== row.revisedBand && ' (changed)'}
                      </span>
                    </TableCell>
                  </TableRow>)}
              </TableBody>
            </Table>
          </div>
        </SectionCard>
      </div>
    </div>;
}